// THE STANDARD - CoachLayout
// Architect Design System: deep black sidebar, gold accents, Barlow Condensed
// Collapsible desktop sidebar (expanded 240px / collapsed 56px icon-only)
// Coach command and personal training share one account, with an explicit mode
// switch so each side stays focused instead of becoming one long drawer.

import { useState, useEffect, useRef, useMemo } from 'react';
import ClientLayout from './ClientLayout';
import { CommandMobileNav, CommandTools } from './CommandMobileNav';
import { WorkspaceSwitch } from './WorkspaceSwitch';
import { Link, useLocation, useSearch } from 'wouter';
import {
  LayoutDashboard, Users, Dumbbell, MessageSquare, Settings, LogOut,
  CalendarCheck, Calendar, Globe, Trophy, BookOpen, FileText,
  ShoppingBag, UserPlus, Loader2, Cross, ClipboardList, CheckSquare,
  BarChart3, Menu, X, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, BookMarked, Sparkles, Search,
  Sun, Apple, Flame, Camera, Ruler, BookHeart, NotebookPen, Microscope, ListTodo,
  Video, Star, History, ShieldCheck, CreditCard, UserCircle, BellRing, GraduationCap, Activity,
  DollarSign, LayoutGrid,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { setNativeSessionToken } from '@/lib/apiBase';
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import { useMessageStream } from '@/lib/messageStream';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import IOSInstallBanner from '@/components/IOSInstallBanner';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Capacitor } from '@capacitor/core';
import { COACH_NAV_GROUPS, mapClientLocationToCoach } from '@/config/standardRoutes';
import { WidePrimaryNav } from './WidePrimaryNav';
import { useInboxStore } from '@/stores/inboxStore';
import '@/styles/premium-app-shell.css';
import '@/styles/responsive-workspace.css';
import '@/styles/system-workspace.css';
import '@/styles/ipad-desktop-workspace.css';
// Phone-only fluid type and spacing; keep it after the other shell styles.
import '@/styles/phone-fluid.css';
// Phone-only page surface (one background, paper cards); after phone-fluid.
import '@/styles/phone-surfaces.css';
// Member screen touch targets (client app and /coach/my).
import '@/styles/member-screens.css';
// Glass on the floating chrome only; switch in ./glassChrome. Keep it last.
import '@/styles/glass-chrome.css';
import { useGlassChrome } from './glassChrome';
import { useBack } from '@/hooks/useBack';
import { recordAppLocation } from '@/lib/appNavigationHistory';
import { PremiumSlimBar } from './PremiumSlimBar';
import { ClientQuickFindDialog, useClientQuickFindShortcut } from '@/components/coach/ClientQuickFind';
import { useSlimChrome } from './useSlimChrome';

// ─── Legacy mode exports (kept for backward-compat) ──────────────────────────
export type AppMode = 'coach' | 'athlete';
export function useAppMode() { return { mode: 'coach' as AppMode, setMode: () => {} }; }

// ─── Unified nav groups (REDESIGN_SPEC.md Phase 1b) ───────────────────────────
// 5 coaching sections (Operate / Coach / Grow / Review / System) in order, then
// "My Training" (coach-as-client surfaces) below. Routes are NOT changed — only
// the visible grouping/labels. A few legacy items (Reminders, Workout Library,
// Brotherhood, Playlists) are appended to their nearest section so no route is
// dropped from the drawer.
const UNIFIED_NAV_GROUPS = COACH_NAV_GROUPS.map((group) => ({
  label: group.label,
  items: group.items.map((route) => ({
    path: route.path,
    label: route.navLabel ?? route.title,
    icon: route.icon!,
  })),
}));

function modeForLocation(location: string): AppMode {
  if (location.startsWith('/community/groups/')) {
    try { return localStorage.getItem('standard-app-mode') === 'athlete' ? 'athlete' : 'coach'; } catch { return 'coach'; }
  }
  return location.startsWith('/coach/my') || location === '/coach/wins' ? 'athlete' : 'coach';
}

function CoachModeSwitch({
  mode,
  onChange,
  compact = false,
}: {
  mode: AppMode;
  onChange: (mode: AppMode) => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={() => onChange(mode === 'coach' ? 'athlete' : 'coach')}
        className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-white/10"
        style={{ color: 'var(--primary)', background: 'rgba(255,255,255,0.035)' }}
        title={mode === 'coach' ? 'Switch to My Training' : 'Switch to Coach Command'}
      >
        {mode === 'coach' ? <UserCircle className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-black/30 p-1" role="group" aria-label="Choose app mode">
      <button
        type="button"
        onClick={() => onChange('athlete')}
        aria-pressed={mode === 'athlete'}
        className="flex min-h-10 items-center justify-center gap-2 rounded-lg px-2 text-[10px] font-black uppercase tracking-[0.14em] transition-all"
        style={{
          background: mode === 'athlete' ? 'var(--primary)' : 'transparent',
          color: mode === 'athlete' ? 'var(--primary-foreground)' : '#8b9297',
          fontFamily: 'Barlow Condensed, sans-serif',
        }}
      >
        <UserCircle className="h-3.5 w-3.5" /> My Training
      </button>
      <button
        type="button"
        onClick={() => onChange('coach')}
        aria-pressed={mode === 'coach'}
        className="flex min-h-10 items-center justify-center gap-2 rounded-lg px-2 text-[10px] font-black uppercase tracking-[0.14em] transition-all"
        style={{
          background: mode === 'coach' ? 'var(--primary)' : 'transparent',
          color: mode === 'coach' ? 'var(--primary-foreground)' : '#8b9297',
          fontFamily: 'Barlow Condensed, sans-serif',
        }}
      >
        <ShieldCheck className="h-3.5 w-3.5" /> Command
      </button>
    </div>
  );
}

// Flat list for badge queries
const NAV_ITEMS = UNIFIED_NAV_GROUPS.flatMap(g => g.items);

const ACCORDION_KEY = 'sidebar-sections-v4';
const DEFAULT_OPEN = ['Operate', 'Coach']; // Only these start expanded

function loadOpenSections(): Set<string> {
  try {
    const raw = localStorage.getItem(ACCORDION_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* noop */ }
  return new Set(DEFAULT_OPEN);
}

function NavItemsWithBadge({
  location,
  collapsed,
  onNavigate,
  groups,
}: {
  location: string;
  collapsed?: boolean;
  onNavigate?: () => void;
  groups: typeof UNIFIED_NAV_GROUPS;
}) {
  const { data: pendingApps = 0 } = trpc.applications.pendingCount.useQuery(undefined, { staleTime: 2 * 60_000 });
  // Use the richer summary endpoint so the badge can deep-link to whoever
  // most recently messaged us instead of dumping the coach on a generic
  // inbox page. The count-only variant stays for any other caller.
  const { data: unreadSummary, refetch: refetchUnread } = trpc.messages.unreadSummary.useQuery(undefined, {
    staleTime: 30_000,
    refetchInterval: 10_000,
  });
  // Live invalidation via SSE — the moment the server publishes a new
  // inbound message for the coach, the badge updates without waiting on
  // the 10s poll.
  useMessageStream('message:new', () => { refetchUnread(); });
  const unreadMessages = unreadSummary?.count ?? 0;
  const unreadFromUserId = unreadSummary?.latestUnreadFromUserId ?? null;
  const unreadFromName = unreadSummary?.latestUnreadFromName ?? null;

  const [openSections, setOpenSections] = useState<Set<string>>(() => loadOpenSections());

  // Auto-expand the section containing the active route
  useEffect(() => {
    for (const group of groups) {
      const hasActive = group.items.some(({ path }) => {
        const isExactRoot = path === '/coach' || path === '/client';
        return isExactRoot ? location === path : location.startsWith(path);
      });
      if (hasActive && !openSections.has(group.label)) {
        setOpenSections(prev => {
          const next = new Set(prev);
          next.add(group.label);
          return next;
        });
      }
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSection = (label: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try { localStorage.setItem(ACCORDION_KEY, JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  };

  const [navSearch, setNavSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Filtered flat list when searching
  const filteredItems = useMemo(() => {
    if (!navSearch.trim()) return null;
    const q = navSearch.toLowerCase();
    const results: typeof groups[0]['items'] = [];
    for (const group of groups) {
      for (const item of group.items) {
        if (item.label.toLowerCase().includes(q) || item.path.toLowerCase().includes(q)) {
          results.push(item);
        }
      }
    }
    return results;
  }, [navSearch, groups]);

  return (
    <div className="space-y-1">
      {/* Sidebar Search */}
      {!collapsed && (
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
            <input
              ref={searchRef}
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Search pages..."
              className="w-full pl-8 pr-7 py-1.5 rounded-lg text-xs bg-transparent outline-none transition-colors"
              style={{
                border: '1px solid rgba(255,255,255,0.06)',
                color: '#d1d5db',
                fontFamily: 'Inter, sans-serif',
              }}
              onFocus={(e) => { (e.target as HTMLElement).style.borderColor = 'rgba(245,158,11,0.3)'; }}
              onBlur={(e) => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)'; }}
            />
            {navSearch && (
              <button
                onClick={() => { setNavSearch(''); searchRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2"
                style={{ color: '#9ca3af' }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Search results (flat list) */}
      {filteredItems ? (
        filteredItems.length === 0 ? (
          <p className="px-3 py-4 text-xs text-center" style={{ color: '#9ca3af' }}>No pages match “{navSearch}”</p>
        ) : (
          <div className="space-y-0.5">
            {filteredItems.map(({ path, label, icon: Icon }) => {
              const isExactRoot = path === '/coach' || path === '/client';
              const isActive = isExactRoot ? location === path : location.startsWith(path);
              return (
                <Link key={path} href={path} onClick={() => { setNavSearch(''); onNavigate?.(); }}>
                  <div
                    className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-150 cursor-pointer"
                    style={{
                      background: isActive ? 'color-mix(in oklch, var(--primary) 8%, transparent)' : 'transparent',
                      borderLeft: isActive ? '2px solid var(--primary)' : '2px solid transparent',
                      paddingLeft: isActive ? '10px' : '12px',
                      color: isActive ? 'var(--primary)' : '#9ca3af',
                    }}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )
      ) : (
      <>
      {groups.map((group) => {
        const isOpen = collapsed || openSections.has(group.label);
        return (
        <div
          key={group.label}
          // Divider above the coach-as-client "My Training" section.
          style={group.label === 'My Training' ? { borderTop: '1px solid var(--hairline)', marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)' } : undefined}
        >
          {/* Collapsible section header */}
          {!collapsed && (
            <button
              type="button"
              onClick={() => toggleSection(group.label)}
              className="w-full flex items-center justify-between px-3 pt-2.5 pb-3 group"
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
            >
              <span
                className="text-[10px] font-black uppercase select-none"
                style={{
                  color: 'var(--gold-500)',
                  fontFamily: 'Barlow Condensed, sans-serif',
                  letterSpacing: '0.35em',
                  opacity: isOpen ? 0.85 : 0.5,
                }}
              >
                {group.label}
              </span>
              <ChevronDown
                className="w-3 h-3 transition-transform duration-200"
                style={{
                  color: 'rgba(245,158,11,0.4)',
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                }}
              />
            </button>
          )}
          {collapsed && <div className="my-1 mx-2 border-t border-white/5" />}
          {/* Collapsible items — simple show/hide instead of maxHeight animation (mobile Safari compat) */}
          {isOpen && (
          <div className="space-y-0.5">
            {group.items.map(({ path, label, icon: Icon }) => {
              const isExactRoot = path === '/coach' || path === '/client';
              const isActive = isExactRoot ? location === path : location.startsWith(path);
              const showAppsBadge = path === '/coach/applications' && (pendingApps as number) > 0;
              const showMsgBadge = path === '/coach/inbox' && (unreadMessages as number) > 0;
              const showBadge = showAppsBadge || showMsgBadge;
              const badgeCount = showAppsBadge ? (pendingApps as number) : (unreadMessages as number);
              // For the Messages row only, override the href to deep-link
              // into the conversation with the most-recent unread sender so
              // the coach lands inside that thread instead of staring at a
              // list and guessing which row to tap.
              const linkHref = (path === '/coach/inbox' && unreadFromUserId)
                ? `/coach/inbox?client=${unreadFromUserId}`
                : path;
              const linkTitle = (path === '/coach/inbox' && unreadFromName)
                ? `${unreadMessages} unread · latest from ${unreadFromName}`
                : (collapsed ? label : undefined);
              return (
                <Link key={path} href={linkHref} onClick={onNavigate}>
                  <div
                    title={linkTitle}
                    className={`flex items-center transition-all duration-150 cursor-pointer relative rounded-xl ${
                      collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-3 px-3 py-2'
                    }`}
                    style={{
                      background: isActive ? 'color-mix(in oklch, var(--primary) 8%, transparent)' : 'transparent',
                      borderLeft: !collapsed && isActive ? '2px solid var(--primary)' : collapsed ? 'none' : '2px solid transparent',
                      paddingLeft: !collapsed ? (isActive ? '10px' : '12px') : undefined,
                      color: isActive ? 'var(--primary)' : '#9ca3af',
                      ...(collapsed && isActive ? { outline: '1px solid color-mix(in oklch, var(--primary) 30%, transparent)' } : {}),
                    }}
                    onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.color = '#d1d5db'; }}
                    onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.color = '#9ca3af'; }}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={isActive ? 2 : 1.5} />
                    {!collapsed && <span className="text-sm font-medium">{label}</span>}
                    {!collapsed && showBadge && (
                      <span className="ml-auto min-w-[18px] h-[18px] px-1 text-black text-[10px] font-black rounded-full flex items-center justify-center"
                        style={{ background: 'var(--primary)' }}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                    {collapsed && showBadge && (
                      <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          )}
        </div>
      );
      })}
      </>
      )}
    </div>
  );
}

function SelfEnrollToggle({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const { data: myProfile, isLoading: profileLoading } = trpc.clients.myProfile.useQuery();

  const selfEnroll = trpc.clients.selfEnroll.useMutation({
    onSuccess: (res) => {
      if (res.enrolled) {
        toast.success('Enrolled! Switching to your client experience.');
        utils.clients.myProfile.invalidate();
      }
      onNavigate?.();
      navigate('/client');
    },
    onError: () => toast.error('Enrollment failed. Try again.'),
  });

  if (profileLoading) {
    return (
      <div className="pt-2 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-3 py-2.5'}`} style={{ color: '#9ca3af' }}>
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          {!collapsed && <span className="text-sm">Checking...</span>}
        </div>
      </div>
    );
  }

  if (myProfile) {
    return null;
  }

  return (
    <div className="pt-2 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <button
        title={collapsed ? 'Enroll as Client' : undefined}
        onClick={() => selfEnroll.mutate()}
        disabled={selfEnroll.isPending}
        className={`flex items-center w-full rounded-xl transition-all duration-150 ${
          collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-3 px-3 py-2.5'
        }`}
        style={{ color: '#9ca3af' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#34d399'; (e.currentTarget as HTMLElement).style.background = 'rgba(52,211,153,0.05)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {selfEnroll.isPending
          ? <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          : <UserPlus className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
        }
        {!collapsed && <span className="text-sm font-medium">Enroll as Client</span>}
      </button>
    </div>
  );
}

interface CoachLayoutProps {
  children: React.ReactNode;
}

export default function CoachLayout({ children }: CoachLayoutProps) {
  const [location, navigate] = useLocation();
  const actualSearch = useSearch();
  const threadFocusActive = useInboxStore((state) => state.threadFocusActive);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [commandToolsOpen, setCommandToolsOpen] = useState(false);
  const [quickFindOpen, setQuickFindOpen] = useState(false);
  const [logoRefreshing, setLogoRefreshing] = useState(false);
  const [mode, setMode] = useState<AppMode>(() => modeForLocation(location));
  const visibleNavGroups = useMemo(
    () => UNIFIED_NAV_GROUPS.filter((group) => mode === 'athlete' ? group.label === 'My Training' : group.label !== 'My Training'),
    [mode],
  );
  const changeMode = (nextMode: AppMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    try { localStorage.setItem('standard-app-mode', nextMode); } catch { /* private mode */ }
    setDrawerOpen(false);
    navigate(nextMode === 'athlete' ? '/coach/my/today' : '/coach');
  };
  const handleBack = useBack('/coach');
  // Personal training mode keeps Cmd+K for its own app directory.
  useClientQuickFindShortcut(setQuickFindOpen, modeForLocation(location) !== 'athlete');
  useEffect(() => { recordAppLocation(location + (actualSearch ? `?${actualSearch}` : '')); }, [location, actualSearch]);
  const handleLogoRefresh = () => {
    if (logoRefreshing) return;
    setLogoRefreshing(true);
    setTimeout(async () => {
      try {
        if ('caches' in window) {
          const keys = await (window as Window & typeof globalThis).caches.keys();
          await Promise.all(keys.map(k => (window as Window & typeof globalThis).caches.delete(k)));
        }
      } catch { /* best-effort */ }
      sessionStorage.setItem('app_just_refreshed', '1');
      window.location.reload();
    }, 300);
  };
  // Show "App updated" toast after a logo-tap refresh
  useEffect(() => {
    if (sessionStorage.getItem('app_just_refreshed')) {
      sessionStorage.removeItem('app_just_refreshed');
      setTimeout(() => toast.success('App updated', { duration: 3000 }), 500);
    }
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('coach_sidebar_collapsed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    const routeMode = modeForLocation(location);
    setMode((current) => current === routeMode ? current : routeMode);
    try { localStorage.setItem('standard-app-mode', routeMode); } catch { /* private mode */ }
  }, [location]);
  const { data: user } = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const { data: pendingApps = 0 } = trpc.applications.pendingCount.useQuery(undefined, { staleTime: 2 * 60_000 });
  // Unread message count — used for nav badge and PWA app icon badge
  // Use the richer summary endpoint so the badge can deep-link to whoever
  // most recently messaged us instead of dumping the coach on a generic
  // inbox page. The count-only variant stays for any other caller.
  const { data: unreadSummary, refetch: refetchUnread } = trpc.messages.unreadSummary.useQuery(undefined, {
    staleTime: 30_000,
    refetchInterval: 10_000,
  });
  // Live invalidation via SSE — the moment the server publishes a new
  // inbound message for the coach, the badge updates without waiting on
  // the 10s poll.
  useMessageStream('message:new', () => { refetchUnread(); });
  const unreadMessages = unreadSummary?.count ?? 0;
  const unreadFromUserId = unreadSummary?.latestUnreadFromUserId ?? null;
  const unreadFromName = unreadSummary?.latestUnreadFromName ?? null;

  // Auto-subscribe coach to push notifications — fully automatic, no manual action required
  // Works on production (setthestandard.fit) only — push service unavailable in preview/sandbox
  // Strategy:
  //   1. If permission already granted → silently re-register ONCE per session (handles reinstalls/stale tokens)
  //   2. If permission not yet asked → show prompt until granted
  //   3. sessionStorage flag prevents the loop from firing on every re-render
  const PUSH_PRODUCTION_DOMAINS = ['setthestandard.fit', 'www.setthestandard.fit'];
  const isNativeApp = Capacitor.isNativePlatform();
  const isPushProduction = isNativeApp || PUSH_PRODUCTION_DOMAINS.includes(window.location.hostname);
  const { isSupported, isSubscribed, isLoading: pushLoading, subscribe } = usePushNotifications();

  useEffect(() => {
    if (!isPushProduction) return;
    if (!isSupported) return;
    if (pushLoading) return;

    // Use sessionStorage so this runs at most ONCE per browser session (tab open).
    // This prevents the subscribe/unsubscribe loop on re-renders while still
    // re-registering on every new session (handles reinstalls and stale tokens).
    const sessionKey = 'push_registered_this_session';
    const alreadyRanThisSession = sessionStorage.getItem(sessionKey);

    if (isNativeApp) {
      if (!alreadyRanThisSession) {
        sessionStorage.setItem(sessionKey, '1');
        subscribe().catch(() => { /* silent — best effort */ });
      }
      return;
    }

    if (typeof Notification === 'undefined') return;

    if (Notification.permission === 'granted') {
      if (!alreadyRanThisSession) {
        sessionStorage.setItem(sessionKey, '1');
        // Silently re-register once per session — forces fresh token, cleans up stale subscriptions
        subscribe().catch(() => { /* silent — best effort */ });
      }
    } else if (Notification.permission === 'default') {
      // Permission not yet granted — show prompt. Sonner deduplicates by id so it only shows once.
      toast('Tap Enable to receive session alerts, check-ins, and messages', {
        id: 'push-permission-prompt',
        duration: Infinity,
        action: {
          label: 'Enable',
          onClick: () => {
            sessionStorage.setItem(sessionKey, '1');
            subscribe().catch(() => { /* silent */ });
          },
        },
      });
    }
    // If permission === 'denied': respect the user's OS choice, do nothing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushLoading, isSupported, isPushProduction, isNativeApp, isSubscribed]);

  // Persist sidebar collapsed state
  useEffect(() => {
    try { localStorage.setItem('coach_sidebar_collapsed', String(sidebarCollapsed)); } catch { /* noop */ }
  }, [sidebarCollapsed]);

  // PWA App Icon Badge API — updates the badge count on the home screen app icon
  // Works on iOS 16.4+ (Safari), Android Chrome, and desktop Chrome/Edge
  useEffect(() => {
    const count = unreadMessages as number;
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        (navigator as Navigator & { setAppBadge: (count: number) => Promise<void> })
          .setAppBadge(count)
          .catch(() => { /* best-effort — permission may not be granted */ });
      } else {
        (navigator as Navigator & { clearAppBadge: () => Promise<void> })
          .clearAppBadge()
          .catch(() => { /* best-effort */ });
      }
    }
  }, [unreadMessages]);

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      setNativeSessionToken(null);
      toast.success('Signed out');
      window.location.href = '/login';
    },
    onError: () => {
      setNativeSessionToken(null);
      window.location.href = '/login';
    },
  });

  const closeDrawer = () => setDrawerOpen(false);
  const shellRef = useRef<HTMLDivElement>(null);
  useSlimChrome(shellRef);
  useGlassChrome(shellRef);

  const sidebarBg = '#111111';
  const sidebarBorder = 'var(--hairline)';
  const isInboxRoute = location.startsWith('/coach/inbox');
  const isFocusedInboxThread = isInboxRoute && threadFocusActive;
  if (modeForLocation(location) === 'athlete') return <ClientLayout coachPersonal>{children}</ClientLayout>;

  return (
    <>
    <div ref={shellRef} className="premium-app-shell premium-coach-shell wide-shell flex overflow-hidden kb-shell" style={{ background: 'var(--premium-canvas)' }}>
      <WidePrimaryNav
        location={location}
        hrefFor={mapClientLocationToCoach}
        user={user}
        workspaceMode="coach"
        onAllTools={() => setCommandToolsOpen(true)}
        onBrandClick={handleLogoRefresh}
        showMessages
        unreadMessages={unreadMessages as number}
      />
      <CommandTools open={commandToolsOpen} onClose={() => setCommandToolsOpen(false)} />
      <ClientQuickFindDialog open={quickFindOpen} onOpenChange={setQuickFindOpen} />
      {/* ── Desktop Sidebar ─────────────────────────────────────────────── */}
      <aside
        className="premium-app-sidebar premium-coach-desktop-sidebar hidden xl:flex flex-shrink-0 flex-col h-full min-h-0 transition-all duration-200"
        style={{
          width: sidebarCollapsed ? '56px' : '240px',
          background: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`,
        }}
      >
        {/* Brand / Collapse toggle */}
        <div
          className="flex items-center flex-shrink-0"
          style={{
            borderBottom: `1px solid ${sidebarBorder}`,
            height: '60px',
            padding: sidebarCollapsed ? '0 8px' : '0 16px',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          }}
        >
          {!sidebarCollapsed && (
            <img
              src={STANDARD_LOGO_SRC}
              alt="The Standard"
              className={`h-8 w-auto object-contain cursor-pointer select-none transition-opacity ${logoRefreshing ? 'opacity-30' : 'hover:opacity-70'}`}
              onClick={handleLogoRefresh}
              title="Tap to refresh app"
            />
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="w-8 h-8 flex items-center justify-center rounded-xl transition-all flex-shrink-0"
            style={{ color: '#9ca3af' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#d1d5db'; (e.currentTarget as HTMLElement).style.background = 'var(--hairline)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        <div className={sidebarCollapsed ? 'px-2 pt-3' : 'px-3 pt-3'}>
          <CoachModeSwitch mode={mode} onChange={changeMode} compact={sidebarCollapsed} />
        </div>

        {/* Nav */}
        <nav className="flex-1 min-h-0 px-2 py-3 overflow-y-auto">
          <NavItemsWithBadge
            location={location}
            collapsed={sidebarCollapsed}
            groups={visibleNavGroups}
          />
          <SelfEnrollToggle collapsed={sidebarCollapsed} />
        </nav>

        {/* User footer */}
        <div
          className="flex-shrink-0 px-2 py-3"
          style={{ borderTop: `1px solid ${sidebarBorder}` }}
        >
          {!sidebarCollapsed ? (
            <>
              {user && (
                <div className="grid grid-cols-2 gap-2 px-1 pb-2">
                  <Link href={`/coach/my/members/${user.id}`}>
                    <div className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-black uppercase tracking-wider transition-colors hover:bg-white/[0.05]" style={{ color: '#d1d5db', border: '1px solid rgba(255,255,255,0.08)', fontFamily: 'Barlow Condensed, sans-serif' }}>
                      <UserCircle className="w-3.5 h-3.5" /> Profile
                    </div>
                  </Link>
                  <Link href="/coach/my/community?view=groups">
                    <div className="flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[10px] font-black uppercase tracking-wider transition-colors hover:bg-white/[0.05]" style={{ color: '#d1d5db', border: '1px solid rgba(255,255,255,0.08)', fontFamily: 'Barlow Condensed, sans-serif' }}>
                      <Users className="w-3.5 h-3.5" /> Groups
                    </div>
                  </Link>
                </div>
              )}
              <div className="flex items-center gap-3 px-3 py-2 mb-1">
                <div className="w-8 h-8 rounded-xl flex-shrink-0 overflow-hidden"
                  style={{ border: '1px solid rgba(245,158,11,0.2)' }}>
                  {(user as any)?.avatarUrl ? (
                    <img src={(user as any).avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"
                      style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                      <span className="text-xs font-black font-mono" style={{ color: 'var(--primary)' }}>
                        {user?.name?.charAt(0)?.toUpperCase() ?? 'C'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: '#d1d5db' }}>{user?.name ?? 'Coach Fe'}</p>
                  <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: '#9ca3af', fontFamily: 'Barlow Condensed, sans-serif' }}>Coach</p>
                </div>
              </div>
              <button
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
                className="flex items-center gap-3 px-3 py-2 w-full rounded-xl transition-all duration-150"
                style={{ color: '#9ca3af' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#d1d5db'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Sign Out</span>
              </button>
            </>
          ) : (
              <div className="flex flex-col items-center gap-2">
              {user && (
                <Link href={`/coach/my/members/${user.id}`}>
                  <div title="My profile" className="w-8 h-8 rounded-xl overflow-hidden cursor-pointer" style={{ border: '1px solid rgba(245,158,11,0.35)' }}>
                    {(user as any)?.avatarUrl ? (
                      <img src={(user as any).avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                        <span className="text-xs font-black font-mono" style={{ color: 'var(--primary)' }}>{user?.name?.charAt(0)?.toUpperCase() ?? 'C'}</span>
                      </div>
                    )}
                  </div>
                </Link>
              )}
              <div className="flex flex-col items-center gap-1">
                <button
                  title="Sign Out"
                  onClick={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                  className="w-8 h-8 flex items-center justify-center rounded-xl transition-all"
                  style={{ color: '#9ca3af' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#d1d5db'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── Mobile Slide-out Drawer ─────────────────────────────────────── */}
      {drawerOpen && (
      <div className="premium-coach-mobile-drawer xl:hidden fixed inset-0 z-[70] flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDrawer} />
          <div className="relative w-72 max-w-[85vw] h-full flex flex-col shadow-2xl"
            style={{ background: sidebarBg, borderRight: `1px solid ${sidebarBorder}` }}>
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-5"
              style={{
                borderBottom: `1px solid ${sidebarBorder}`,
                paddingTop: 'max(20px, calc(env(safe-area-inset-top, 0px) + 12px))',
              }}
            >
              <img
                src={STANDARD_LOGO_SRC}
                alt="The Standard"
                className={`w-36 object-contain cursor-pointer select-none transition-opacity ${logoRefreshing ? 'opacity-30' : 'active:opacity-50'}`}
                onClick={handleLogoRefresh}
                title="Tap to refresh app"
              />
              <button
                onClick={closeDrawer}
                className="w-8 h-8 flex items-center justify-center rounded-xl transition-all"
                style={{ color: '#9ca3af' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#d1d5db'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav. The top bar's Training and Command switch is the only mode
                switch, so the drawer lists Command pages and tools only. */}
            <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
              <Link href={mode === 'coach' ? '/coach/vault' : '/coach/my/programs'} onClick={closeDrawer}>
                <div
                  className="flex items-center gap-3 px-3 py-2.5 mb-3 rounded-xl active:scale-[0.99] transition-transform"
                  style={{ background: 'color-mix(in oklch, var(--primary) 12%, transparent)', border: '1px solid rgba(245,158,11,0.3)' }}
                >
                  {mode === 'coach'
                    ? <BookMarked className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                    : <Dumbbell className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--primary)' }} />}
                  <div className="min-w-0">
                    <p className="text-sm font-bold leading-tight" style={{ color: 'var(--primary)' }}>{mode === 'coach' ? 'The Vault' : 'My Programs'}</p>
                    <p className="text-[10px] uppercase tracking-widest leading-tight" style={{ color: '#9ca3af', fontFamily: 'Barlow Condensed, sans-serif' }}>{mode === 'coach' ? 'Resource Library' : 'Plans and catalog'}</p>
                  </div>
                </div>
              </Link>
              {mode === 'coach' && (
                <button
                  type="button"
                  onClick={() => { closeDrawer(); setCommandToolsOpen(true); }}
                  className="mb-3 flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left active:scale-[0.99] transition-transform"
                  style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <LayoutGrid className="w-5 h-5 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.72)' }} aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-bold leading-tight" style={{ color: 'rgba(255,255,255,0.9)' }}>All coaching tools</span>
                    <span className="block text-[10px] uppercase tracking-widest leading-tight" style={{ color: '#9ca3af', fontFamily: 'Barlow Condensed, sans-serif' }}>Search every tool</span>
                  </span>
                </button>
              )}
              {user && (
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Link href={`/coach/my/members/${user.id}`} onClick={closeDrawer}>
                    <div className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.88)' }}>
                      <UserCircle className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>My Profile</span>
                    </div>
                  </Link>
                  <Link href="/coach/my/community?view=groups" onClick={closeDrawer}>
                    <div className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.88)' }}>
                      <Users className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Groups</span>
                    </div>
                  </Link>
                </div>
              )}
              <NavItemsWithBadge
                location={location}
                onNavigate={closeDrawer}
                groups={visibleNavGroups}
              />
              <SelfEnrollToggle onNavigate={closeDrawer} />
            </nav>

            {/* User + Logout */}
            <div className="px-3 py-4" style={{ borderTop: `1px solid ${sidebarBorder}`, paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
              <div className="flex items-center gap-3 px-3 py-2 mb-1">
                <div className="w-8 h-8 rounded-xl flex-shrink-0 overflow-hidden"
                  style={{ border: '1px solid rgba(245,158,11,0.2)' }}>
                  {(user as any)?.avatarUrl ? (
                    <img src={(user as any).avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"
                      style={{ background: 'color-mix(in oklch, var(--primary) 15%, transparent)' }}>
                      <span className="text-xs font-black font-mono" style={{ color: 'var(--primary)' }}>
                        {user?.name?.charAt(0)?.toUpperCase() ?? 'C'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate" style={{ color: '#d1d5db' }}>{user?.name ?? 'Coach Fe'}</p>
                  <p className="text-[9px] font-black uppercase tracking-widest" style={{ color: '#9ca3af', fontFamily: 'Barlow Condensed, sans-serif' }}>Coach</p>
                </div>
              </div>
              <button
                onClick={() => { closeDrawer(); logoutMutation.mutate(); }}
                disabled={logoutMutation.isPending}
                className="flex items-center gap-3 px-3 py-2 w-full rounded-xl transition-all duration-150"
                style={{ color: '#9ca3af' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#d1d5db'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#9ca3af'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          </div>
      </div>
      )}

      {/* ── Desktop Sidebarea ───────────────────────────────────────────── */}
      <div className="relative flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Desktop top bar */}
        <header
          className="premium-app-topbar premium-coach-desktop-topbar glass-top-bar hidden xl:flex items-center justify-between px-4 flex-shrink-0 gap-2"
          style={{
            background: sidebarBg,
            borderBottom: `1px solid ${sidebarBorder}`,
            height: '48px',
          }}
        >
          <nav className="standard-recovery-bar flex min-w-0 items-center gap-2" aria-label="Page navigation">
            {location !== '/coach' ? (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition-all hover:bg-white/[0.04] active:scale-95"
                style={{ color: '#f7f5ef', fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Back
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navigate('/coach')}
              aria-current={location === '/coach' ? 'page' : undefined}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] transition-all hover:bg-white/[0.04] active:scale-95"
              style={{
                color: location === '/coach' ? '#b28a49' : '#f7f5ef',
                fontFamily: 'Barlow Condensed, sans-serif',
              }}
            >
              <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
              Dashboard
            </button>
          </nav>
          <div className="flex items-center justify-end gap-2">
            {user && (
              <>
                <Link href="/coach/my/community?view=groups">
                  <button className="inline-flex items-center gap-2 px-3 text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: location.startsWith('/coach/my/community') ? 'var(--primary)' : '#f7f5ef', fontFamily: 'Barlow Condensed, sans-serif' }} title="My groups">
                    <Users className="w-4 h-4" /> Groups
                  </button>
                </Link>
                <Link href={`/coach/my/members/${user.id}`}>
                  <button className="inline-flex items-center gap-2 px-3 text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: location.startsWith(`/coach/my/members/${user.id}`) ? 'var(--primary)' : '#f7f5ef', fontFamily: 'Barlow Condensed, sans-serif' }} title="My personal profile">
                    <UserCircle className="w-4 h-4" /> My Profile
                  </button>
                </Link>
              </>
            )}
            {(pendingApps as number) > 0 && (
              <Link href="/coach/applications">
                <span className="w-5 h-5 text-black text-[10px] font-black rounded-full flex items-center justify-center"
                  style={{ background: 'var(--primary)' }}>
                  {(pendingApps as number) > 9 ? '9+' : pendingApps}
                </span>
              </Link>
            )}
            <Link href="/coach/inbox">
              <button
                className="relative flex items-center justify-center w-9 h-9 rounded-xl transition-colors"
                style={{ color: location.startsWith('/coach/inbox') ? 'var(--primary)' : '#f7f5ef' }}
                title="Messages"
              >
                <MessageSquare className="w-4.5 h-4.5" strokeWidth={1.5} />
                {(unreadMessages as number) > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 text-black text-[9px] font-black rounded-full flex items-center justify-center"
                    style={{ background: 'var(--primary)', lineHeight: 1 }}
                  >
                    {(unreadMessages as number) > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </button>
            </Link>
            <NotificationBell />
          </div>
        </header>
        <PremiumSlimBar
          className={`premium-coach-mobile-topbar glass-top-bar xl:hidden flex-shrink-0${isFocusedInboxThread ? ' premium-coach-mobile-topbar--thread' : ''}`}
          style={{
            paddingTop: 'max(12px, env(safe-area-inset-top, 0px))',
            paddingBottom: '6px',
          }}
          showBack={location !== '/coach'}
          onBack={handleBack}
          onDashboard={() => navigate('/coach')}
          dashboardCurrent={location === '/coach'}
          modeSwitch={<WorkspaceSwitch mode="coach" />}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="w-9 h-9 flex items-center justify-center rounded-xl active:opacity-60"
            style={{ color: '#f7f5ef' }}
          >
            <Menu className="w-5 h-5" />
          </button>
          <img
            src={STANDARD_LOGO_SRC}
            alt="The Standard"
            className={`object-contain cursor-pointer select-none transition-opacity ${logoRefreshing ? 'opacity-30' : 'active:opacity-50'}`}
            onClick={handleLogoRefresh}
            title="Tap to refresh app"
          />
          <button
            type="button"
            onClick={() => setQuickFindOpen(true)}
            aria-label="Find a client"
            aria-keyshortcuts="Control+K Meta+K"
            className="premium-coach-find w-9 h-9 flex items-center justify-center rounded-xl active:opacity-60"
            style={{ color: '#f7f5ef' }}
          >
            <Search className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" />
          </button>
          {(pendingApps as number) > 0 && (
            <Link href="/coach/applications">
              <span className="w-5 h-5 text-black text-[10px] font-black rounded-full flex items-center justify-center"
                style={{ background: 'var(--primary)' }}>
                {(pendingApps as number) > 9 ? '9+' : pendingApps}
              </span>
            </Link>
          )}
          <Link href="/coach/inbox" className="premium-coach-bar-inbox">
            <button
              className="relative flex items-center justify-center w-9 h-9 rounded-xl transition-colors active:opacity-60"
              style={{ color: location.startsWith('/coach/inbox') ? 'var(--primary)' : '#f7f5ef' }}
              title="Messages"
            >
              <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
              {(unreadMessages as number) > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 text-black text-[9px] font-black rounded-full flex items-center justify-center"
                  style={{ background: 'var(--primary)', lineHeight: 1 }}
                >
                  {(unreadMessages as number) > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>
          </Link>
          <NotificationBell />
        </PremiumSlimBar>

        {/* iOS Safari users (coach + dual-role) need to install to Home
            Screen for push to deliver. Banner is no-op on Android +
            standalone PWAs. */}
        {!isFocusedInboxThread && <IOSInstallBanner />}
        <main
          className={`premium-app-content flex-1 min-h-0 xl:pb-0 [-webkit-overflow-scrolling:touch] coach-app-scroll ${
            isInboxRoute
              ? `overflow-hidden premium-inbox-viewport ${isFocusedInboxThread ? 'premium-inbox-viewport--focused' : ''}`
              : 'overflow-y-auto kb-padgap'
          }`}
        >
          {children}
        </main>

        {!isFocusedInboxThread && <CommandMobileNav location={location} unreadMessages={unreadMessages as number} />}
      </div>
    </div>
    </>
  );
}
